import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import type { ICellRendererParams } from 'ag-grid-community';
import SearchVolumeChart from '@/app/common/components/CellRenderers/SearchVolumeChart';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import { decodeGetter } from '@/app/common/components/ag-grid/configs';
import numberHumanizer from '@/helpers/numberHumanizer';
import { getColor, isFirstRow, isLastRow } from '@/helpers/utils';
import { Table } from 'react-bootstrap';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import Divider from '@/components/common/Divider';
import SubtleBadge from '@/components/common/SubtleBadge';
import { commonTooltips } from '@/app/common/tooltip/commonTooltips';
import { keywordResearchTooltips } from '@/app/KeywordResearch/tooltip/keywordResearchTooltips';
import { intentMap, purposeMap } from './keywordGapColumnConfig';
import { difficulyRanges, serpDifficuly } from '@/helpers/serpDifficulity';
import type { MassiveKeywordItem, MassiveSource } from '@/app/KeywordResearch/types/massiveApiTypes';
import AiLogo from '@/assets/img/artificial-intelligence.svg?react';
import { BiLogoBing, BiLogoGoogle, BiLogoYoutube } from 'react-icons/bi';

const menuTabs = ['columnsMenuTab', 'filterMenuTab', 'generalMenuTab'];

type MassiveDetailRow = MassiveKeywordItem;

const iconStyle = {
  width: '18px',
  padding: 0,
  height: '18px',
  flexShrink: 0
} as const;

const toSourceList = (value: MassiveSource | MassiveSource[] | null | undefined) =>
  Array.isArray(value) ? value : value ? [value] : [];

const SourceIcons = ({
  sources
}: {
  sources: MassiveSource[];
}) => (
  <div className="d-flex align-items-center h-100 gap-1">
    {sources.includes('google') && (
      <BiLogoGoogle style={iconStyle} size="18px" title="google" />
    )}
    {sources.includes('youtube') && (
      <BiLogoYoutube style={iconStyle} size="20px" title="youtube" />
    )}
    {sources.includes('bing') && (
      <BiLogoBing style={iconStyle} size="20px" title="bing" />
    )}
  </div>
);

export const massiveDetailColumnConfig = [
  {
    filterValueGetter: decodeGetter,
    field: 'keyword',
    tooltip: keywordResearchTooltips.massive.massive_details_columns.keyword,
    type: 'string',
    title: 'کلمه',
    flex: 1,
    width: 270,
    minWidth: 170,
    searchable: true,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    menuTabs,
    valueParser: (params: { newValue: string }) =>
      decodeURIComponent(params.newValue)
  },
  {
    filterValueGetter: decodeGetter,
    field: 'len',
    tooltip: keywordResearchTooltips.massive.massive_details_columns.len,
    type: 'number',
    title: 'طول کلمه',
    width: 135,
    searchable: true,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    menuTabs
  },
  {
    gp: 'جستجو',
    field: 'search_volume_miztols',
    type: 'number',
    title: 'میزان جستجو',
    width: 145,
    searchable: true,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    menuTabs,
    tooltip:
      'میزان تعداد جستجوی ماهانه این کلمه که توسط ابزار میزفا تولز محاسبه شده.',
    cellRenderer: (params: ICellRendererParams<MassiveDetailRow>) =>
      !params.value ? (
        <HelpTooltip html={commonTooltips.searchVolume} />
      ) : (
        <div className="d-flex align-items-center gap-1">
          {numberHumanizer(params.value)}
        </div>
      )
  },
  {
    gp: 'جستجو',
    field: 'search_volume_dates',
    type: 'array',
    title: 'روند جستجو',
    tooltip: 'روند جستجوی کلمه در یک سال رو در قالب نمودار بهت نمایش میده.',
    width: 130,
    cellStyle: {
      textAlign: 'center',
      margin: '0px'
    },
    cellClass: 'over-flow',
    noFilter: true,
    sortable: false,
    searchable: false,
    menuTabs,
    cellRenderer: (params: ICellRendererParams<MassiveDetailRow>) =>
      params.value ? (
        <SearchVolumeChart
          isFirstRow={isFirstRow(params)}
          isLastRow={isLastRow(params)}
          count={22}
          dates={params.value}
          series={[
            {
              name: 'روند جستجو',
              data: (params.data?.search_volume_values ?? []).map((value, i) => ({
                name: params.value[i],
                value,
                itemStyle: {
                  color: getColor('navbar-vertical-default-link-active-color')
                }
              }))
            }
          ]}
        />
      ) : (
        ''
      )
  },
  {
    field: 'difficulty',
    gp: 'سنجه‌های کلمه',
    suppressHeaderMenuButton: true,
    type: 'number',
    title: 'سختی',
    tooltip:
      'هرچه مقدار بالاتر باشد، کسب رتبه در بین ۱۰ نتیجه اول گوگل دشوارتر خواهد بود.',
    width: 90,
    searchable: true,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    menuTabs,
    cellRenderer: (params: ICellRendererParams<MassiveDetailRow>) =>
      params.value != null && (
        <div className="d-flex align-items-center justify-content-center">
          <ChildrensTooltip
            placement="top"
            html={
              <div>
                سختی کلمه کلیدی
                <Divider />
                <Table borderless size="sm">
                  <tbody>
                    {difficulyRanges.map(([start, end]) => (
                      <tr
                        className={classNames({
                          'fw-black fs--1 position-relative':
                            serpDifficuly(start).color ===
                            serpDifficuly(params.value).color
                        })}
                        key={start}
                      >
                        <td className="d-flex align-items-center gap-1">
                          {serpDifficuly(start).color ===
                            serpDifficuly(params.value)?.color && (
                            <FontAwesomeIcon
                              style={{
                                right: 3,
                                top: '50%',
                                transform: 'translateY(-50%)'
                              }}
                              icon="chevron-left"
                              className="position-absolute text-primary"
                              size="sm"
                            />
                          )}
                          <span
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              direction: 'ltr',
                              height: '20px',
                              width: '28px',
                              fontSize: '12px',
                              backgroundColor: serpDifficuly(start).color
                            }}
                            className="fs--2 px-2 py-0.5 text-white rounded-2"
                          >
                            {start}
                          </span>
                          <FontAwesomeIcon icon="minus" />
                          <span
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              direction: 'ltr',
                              height: '20px',
                              width: '28px',
                              fontSize: '12px',
                              backgroundColor: serpDifficuly(start).color
                            }}
                            className="fs--2 px-2 py-0.5 text-white rounded-2"
                          >
                            {end}
                          </span>
                        </td>
                        <td className="text-white">
                          {serpDifficuly(start).label}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            }
          >
            <div className="fs-1 rounded-2 w-full" style={{ fontWeight: 'bold' }}>
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  direction: 'ltr',
                  marginTop: '6px',
                  height: '20px',
                  width: '28px',
                  fontSize: '12px',
                  backgroundColor: serpDifficuly(params.value).color
                }}
                className="fs--2 px-2 py-0.5 text-white rounded-2"
              >
                {params.value}
              </span>
            </div>
          </ChildrensTooltip>
        </div>
      )
  },
  {
    field: 'intent',
    gp: 'سنجه‌های کلمه',
    suppressHeaderMenuButton: true,
    type: 'select',
    icon: (
      <AiLogo
        style={{
          width: 13,
          marginTop: 6,
          fill: 'var(--ag-header-foreground-color)'
        }}
      />
    ),
    title: 'نیت',
    width: 90,
    searchable: true,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    menuTabs,
    cellRenderer: (params: ICellRendererParams<MassiveDetailRow>) => (
      <div className="w-100 d-flex h-100 align-items-center justify-content-start gap-2">
        {params.value?.map((intent: string, i: number) => (
          <ChildrensTooltip
            placement="top"
            key={i}
            delayToRemove={0}
            html={intentMap[intent]?.tooltip}
          >
            <SubtleBadge
              pill
              bg={intentMap[intent]?.variant}
              className="d-flex align-items-center gap-1 fs--2"
            >
              {intent}
            </SubtleBadge>
          </ChildrensTooltip>
        ))}
      </div>
    ),
    tooltip: (
      <div className="text-start">
        نیت جستجو (Search Intent) یعنی قصد کاربر از جستجوی یک کلمه کلیدی چیه؟
        داده‌های این ستون از هوش مصنوعی گرفته می‌شود.
      </div>
    )
  },
  {
    field: 'purpose',
    gp: 'سنجه‌های کلمه',
    suppressHeaderMenuButton: true,
    type: 'select',
    width: 90,
    title: 'هدف',
    icon: (
      <AiLogo
        style={{
          width: 13,
          marginTop: 6,
          fill: 'var(--ag-header-foreground-color)'
        }}
      />
    ),
    searchable: true,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    menuTabs,
    cellRenderer: (params: ICellRendererParams<MassiveDetailRow>) => (
      <div className="w-100 d-flex h-100 align-items-center justify-content-start gap-2">
        {params.value
          ?.filter((purpose: string) => purposeMap[purpose])
          .map((purpose: string, i: number) => (
            <SubtleBadge
              pill
              key={i}
              bg="info"
              className="d-flex align-items-center gap-1 fs--2"
            >
              {purposeMap[purpose]}
            </SubtleBadge>
          ))}
      </div>
    ),
    tooltip:
      'پیشنهاد می‌دهد که کلمه برای چه نوع محتوایی مناسب‌تر است. داده‌های این بخش از هوش مصنوعی گرفته می‌شود.'
  },
  {
    field: 'parent_keyword',
    tooltip:
      keywordResearchTooltips.massive.massive_details_columns.parent_keyword,
    type: 'string',
    title: 'کلمه والد',
    width: 200,
    searchable: true,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    filterValueGetter: decodeGetter,
    menuTabs
  },
  {
    filterValueGetter: decodeGetter,
    field: 'grand_parent_keyword',
    tooltip:
      keywordResearchTooltips.massive.massive_details_columns
        .grand_parent_keyword,
    type: 'string',
    title: 'کلمه ریشه',
    width: 250,
    searchable: true,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    menuTabs
  },
  {
    field: 'source',
    tooltip: keywordResearchTooltips.massive.massive_details_columns.source,
    type: 'string',
    title: 'منبع',
    width: 95,
    searchable: false,
    floatingFilter: false,
    menuTabs,
    cellRenderer: (params: ICellRendererParams<MassiveDetailRow>) => (
      <SourceIcons sources={toSourceList(params.value)} />
    )
  }
];
